import React, { useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { captureAttributionFromUrl } from '../utils/attribution';
import { trackCampaignEvent, registerUrlWithUtm } from '../utils/campaign';

const AVAS = {
  marcus: {
    name: 'Marcus', img: '/avas/marcus.jpg', role: 'Careers & School',
    headline: 'Rehearse the teacher conversation with Marcus',
    sub: '"I can\'t talk to them, it\'s awkward." Marcus is where teens practise raising a mark dispute, speaking up to a teacher, or their first interview answer — until it stops being scary.',
    bullets: ['Practise the exact words before the real conversation', 'Speak up to authority figures politely and firmly', 'Early interview practice, zero judgment'],
  },
  chloe: {
    name: 'Chloe', img: '/avas/chloe.jpg', role: 'Friendships & Conflict',
    headline: 'Talk through the friend drama with Chloe',
    sub: 'Group chats, silent exclusions, peer pressure. Chloe gives teens a zero-judgment space to figure out their thoughts before they hit "send" in real life.',
    bullets: ['How to say "no" without losing the friend', 'Handling being left out of plans', 'Navigating awkward group dynamics safely'],
  },
  rebecca: {
    name: 'Rebecca', img: '/avas/rebecca.png', role: 'Knowledge Tutor',
    headline: 'Ask the "stupid question" safely with Rebecca',
    sub: 'Some teens would rather fail a question than ask one in class. Rebecca teaches Socratic-style — she never hands over the answer, she asks the next question until your teen finds it themselves.',
    bullets: ['Low-pressure homework and study support', 'Builds the confidence to ask questions in class', 'Socratic method — answers earned, not given'],
  },
};

export default function StartAvA() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const ava = AVAS[(slug || '').toLowerCase()];

  useEffect(() => {
    if (!ava) return;
    captureAttributionFromUrl();
    trackCampaignEvent('landing_view', `/start/${slug.toLowerCase()}`);
  }, [ava, slug]);

  if (!ava) return <Navigate to="/pre-launch" replace />;

  const startFree = () => {
    trackCampaignEvent('cta_click', `/start/${slug.toLowerCase()}`);
    navigate(registerUrlWithUtm());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950 text-white flex flex-col">
      <nav className="flex items-center justify-between px-4 sm:px-6 py-5 max-w-5xl mx-auto w-full">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-14 cursor-pointer" onClick={() => navigate('/pre-launch')} />
        <span className="text-xs text-white/50">GDPR · COPPA · SOC 2-ready</span>
      </nav>

      <section className="flex-1 px-4 py-10 max-w-3xl mx-auto text-center" data-testid={`start-ava-${slug}`}>
        <img src={ava.img} alt={ava.name} className="w-28 h-28 rounded-full object-cover mx-auto mb-5 ring-2 ring-emerald-400 ring-offset-4 ring-offset-gray-950" />
        <div className="inline-flex items-center gap-1 px-3 py-1 mb-4 text-[11px] font-extrabold tracking-wider rounded-full bg-emerald-500 text-white"><Check size={11} /> TRY FOR FREE</div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-4">{ava.headline}</h1>
        <p className="text-lg text-white/70 max-w-xl mx-auto mb-8">{ava.sub}</p>

        <div className="max-w-md mx-auto text-left space-y-3 mb-10">
          {ava.bullets.map(b => (
            <div key={b} className="flex items-start gap-3 p-3 bg-white/5 rounded-xl border border-white/10">
              <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-white/80">{b}</span>
            </div>
          ))}
        </div>

        <button
          onClick={startFree}
          className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:shadow-xl hover:shadow-purple-500/25 transition-all inline-flex items-center gap-2"
          data-testid="start-ava-cta"
        >
          Start a free session with {ava.name} <ArrowRight size={20} />
        </button>
        <p className="text-sm text-white/50 mt-4">15 free minutes · 3 specialised AvAs · £0 forever · no card required</p>
        <p className="text-xs text-white/40 mt-6">
          Also free to try: {Object.values(AVAS).filter(a => a.name !== ava.name).map(a => a.name).join(' & ')}
        </p>
      </section>

      <footer className="px-4 py-6 text-center text-xs text-white/30 border-t border-white/10">
        HiBFF exists to help you need it less.
      </footer>
    </div>
  );
}
